import React, { useEffect, useMemo, useRef, useState } from 'react';
import ModuleTopExtras from '../ModuleTopExtras';
import { useLanguage } from '../../i18n/LanguageContext';
import './DictationModule.css';

// ---------------------------------------------------------------------------
// Dictation module — 100% in-browser.
// No AI and no third-party API:
//   • sentence splitting is done locally (EN/ZH commas & full stops),
//   • listening uses window.speechSynthesis (Web Speech API, device voices),
//   • answers are checked locally with a word/character diff.
// All sentences are shown on one page; each has a Listen button, its own
// answer box and a Reveal toggle. Nothing is uploaded or saved.
// ---------------------------------------------------------------------------

const speechSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

interface LangOption { value: string; label: string }
const LANG_OPTIONS: LangOption[] = [
  { value: 'en-US', label: 'English (en-US)' },
  { value: 'en-GB', label: 'English (en-GB)' },
  { value: 'zh-HK', label: '粵語 / 香港中文 (zh-HK)' },
  { value: 'zh-TW', label: '繁體中文 (zh-TW)' },
  { value: 'zh-CN', label: '简体中文 (zh-CN)' },
];

// Split a passage into sentences on EN/ZH commas, full stops and related marks.
// Newlines also separate. The delimiters themselves are dropped.
const SPLIT_RE = /[,.;:!?，。！？；：、\n\r]+/;

function splitSentences(text: string): string[] {
  return text
    .split(SPLIT_RE)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

const HAN_RE = /\p{Script=Han}/u;
const hasHan = (s: string) => HAN_RE.test(s);

// Tokenise for diff: each Han character is its own token; other letters/numbers
// are grouped into words (so mixed sentences like 「我喜歡 apple」 work too).
function tokenize(s: string): string[] {
  return s.toLowerCase().match(/\p{Script=Han}|[\p{L}\p{N}]+/gu) ?? [];
}

type DiffToken = { text: string; cls: 'ok' | 'missing' | 'extra' };

// Space between tokens only when a side is a multi-char (Latin) word, so
// 「我喜歡 apple」reads naturally instead of 「我 喜 歡 apple」.
const tokenGap = (a: string, b: string) => (a.length > 1 || b.length > 1 ? ' ' : '');

// LCS-based diff between the target and the answer.
function diffTokens(target: string, answer: string): { target: DiffToken[]; answer: DiffToken[]; exact: boolean } {
  const a = tokenize(target);
  const b = tokenize(answer);
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const outA: DiffToken[] = [];
  const outB: DiffToken[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      outA.push({ text: a[i], cls: 'ok' });
      outB.push({ text: b[j], cls: 'ok' });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      outA.push({ text: a[i], cls: 'missing' });
      i++;
    } else {
      outB.push({ text: b[j], cls: 'extra' });
      j++;
    }
  }
  while (i < m) outA.push({ text: a[i++], cls: 'missing' });
  while (j < n) outB.push({ text: b[j++], cls: 'extra' });
  const exact = outA.every((t) => t.cls === 'ok') && outB.every((t) => t.cls === 'ok');
  return { target: outA, answer: outB, exact };
}

interface RowState {
  answer: string;
  revealed: boolean;   // answer panel open?
  correct: boolean;    // result of the latest reveal/check
}

// Sample lines spoken by the setup-page “Test voice” button.
const VOICE_SAMPLES: Record<string, string> = {
  en: 'Hello! This is a dictation voice test. If you can hear me clearly, you are ready to start.',
  zh: '你好，這是聽寫語音測試。如果你聽得清楚，就可以開始了。',
};

// Pick the nicest voice for a language: a Google voice first (they sound the
// clearest), preferring the female one, then any female system voice.
const FEMALE_RE = /female|woman|girl|samantha|victoria|karen|moira|tessa|fiona|veena|zira|susan|allison|ava|serena|annie|mei|女/i;
const MALE_RE = /male|man|boy|daniel|alex|fred|david|mark|oliver|thomas|arthur|aaron|gordon|男/i;

function pickVoice(available: SpeechSynthesisVoice[], langCode: string): SpeechSynthesisVoice | null {
  if (!available.length) return null;
  const base = langCode.split('-')[0];
  const sameLang = (v: SpeechSynthesisVoice) => v.lang === langCode || v.lang?.startsWith(base);
  const ranked = [...available].sort((a, b) => {
    const score = (v: SpeechSynthesisVoice) => {
      let s = 0;
      if (sameLang(v)) s += 100;
      if (/google/i.test(v.name)) s += 50;
      if (FEMALE_RE.test(v.name)) s += 25;
      if (MALE_RE.test(v.name)) s -= 25;
      // Exact locale match beats a sibling (e.g. zh-HK over zh-TW).
      if (v.lang === langCode) s += 10;
      return s;
    };
    return score(b) - score(a);
  });
  // Require a language match if one exists; otherwise fall back to any voice.
  return ranked.find(sameLang) ?? ranked[0] ?? null;
}

const DictationModule: React.FC = () => {
  const { t } = useLanguage();
  // ---- Setup state ----
  const [passage, setPassage] = useState('');
  const [lang, setLang] = useState('en-US');
  const [started, setStarted] = useState(false);
  const [slow, setSlow] = useState(false);

  // Available device voices and the auto-picked (preferred Google female) one.
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [voice, setVoice] = useState<SpeechSynthesisVoice | null>(null);

  const sentences = useMemo(() => splitSentences(passage), [passage]);

  // Voices load asynchronously in most browsers.
  useEffect(() => {
    if (!speechSupported) return;
    const load = () => setVoices(window.speechSynthesis.getVoices());
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  // Re-pick the preferred voice whenever the language or voice list changes.
  useEffect(() => {
    if (!speechSupported) return;
    setVoice((prev) => {
      const base = lang.split('-')[0];
      if (prev && (prev.lang === lang || prev.lang?.startsWith(base))) return prev;
      return pickVoice(voices, lang);
    });
  }, [voices, lang]);

  // Auto-pick a spoken language from the pasted text (only while not started).
  useEffect(() => {
    if (started) return;
    if (hasHan(passage)) {
      setLang((prev) => (prev.startsWith('zh') ? prev : 'zh-HK'));
    } else {
      setLang((prev) => (prev.startsWith('zh') ? 'en-US' : prev));
    }
  }, [passage, started]);

  // ---- Practice state (one row per sentence) ----
  const [rows, setRows] = useState<RowState[]>([]);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const speak = (text: string) => {
    if (!speechSupported || !text) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    if (voice) {
      u.voice = voice;
      u.lang = voice.lang;
    } else {
      u.lang = lang;
    }
    u.rate = slow ? 0.65 : hasHan(text) ? 0.85 : 0.95;
    window.speechSynthesis.speak(u);
  };

  // Speak a short sample in the current language/voice on the setup page.
  const testVoice = () => {
    if (!speechSupported) return;
    const sample = VOICE_SAMPLES[lang.split('-')[0]] ?? VOICE_SAMPLES.en;
    speak(sample);
  };

  useEffect(() => () => {
    if (speechSupported) window.speechSynthesis.cancel();
  }, []);

  const start = () => {
    if (sentences.length === 0) return;
    setRows(sentences.map(() => ({ answer: '', revealed: false, correct: false })));
    setStarted(true);
  };

  const backToSetup = () => {
    if (speechSupported) window.speechSynthesis.cancel();
    setStarted(false);
  };

  const setAnswer = (i: number, value: string) => {
    setRows((prev) => {
      const next = [...prev];
      // Editing after a check invalidates the previous result.
      next[i] = { ...next[i], answer: value, revealed: false, correct: false };
      return next;
    });
  };

  const toggleReveal = (i: number) => {
    setRows((prev) => {
      const next = [...prev];
      const row = next[i];
      const opening = !row.revealed;
      const diff = opening ? diffTokens(sentences[i], row.answer) : null;
      next[i] = { ...row, revealed: opening, correct: opening ? !!diff?.exact : row.correct };
      return next;
    });
  };

  const clearAnswers = () => {
    if (speechSupported) window.speechSynthesis.cancel();
    setRows(sentences.map(() => ({ answer: '', revealed: false, correct: false })));
  };

  const checkedCount = rows.filter((r) => r.revealed).length;
  const correctCount = rows.filter((r) => r.revealed && r.correct).length;
  const progressPct = rows.length ? (checkedCount / rows.length) * 100 : 0;

  // ------------------------------------------------------------------ setup
  if (!started) {
    return (
      <div className="module-container dictation-module">
        <div className="module-header">
          <span className="module-icon">🎯</span>
          <h2 className="module-title">{t('moduleHeaders.dictation.title')}</h2>
          <p className="module-subtitle">{t('moduleHeaders.dictation.subtitle')}</p>
        </div>
        <ModuleTopExtras moduleId="dictation" />
        <div className="module-content">
          <div className="content-section">
            <div className="section-header">
              <h3>Your passage</h3>
              <p>
                Your passage is split into sentences automatically — each sentence becomes
                one dictation item.
              </p>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="dictation-lang">Speech language</label>
              <div className="dict-lang-row">
                <select
                  id="dictation-lang"
                  className="form-input form-select"
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                >
                  {LANG_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={testVoice}
                  disabled={!speechSupported}
                >
                  🔊 Test voice
                </button>
              </div>
              {speechSupported && (
                <p className="dict-voice-name">
                  Voice: {voice?.name ?? 'system default'}
                </p>
              )}
            </div>

            <textarea
              id="dictation-passage"
              className="content-textarea"
              rows={10}
              placeholder={
                'Paste your text here…\n\nExample:\nThe early bird catches the worm, but the second mouse gets the cheese.\n勤有功，戲無益，戒之哉，宜勉力。'
              }
              value={passage}
              onChange={(e) => setPassage(e.target.value)}
            />

            {passage.trim() && (
              <p className="dict-sentence-count">
                {sentences.length} sentence{sentences.length === 1 ? '' : 's'} detected
              </p>
            )}

            {!speechSupported && (
              <div className="dict-warning">
                ⚠️ Your browser does not support speech playback. Use a recent Chrome, Edge or Safari for the
                listen button.
              </div>
            )}

            <div className="generation-actions" style={{ flexDirection: 'column', gap: '12px' }}>
              <button className="btn btn-primary" onClick={start} disabled={sentences.length === 0}>
                Start dictation ({sentences.length})
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------- practice
  return (
    <div className="module-container dictation-module">
      <div className="module-header">
        <span className="module-icon">🎯</span>
        <h2 className="module-title">{t('moduleHeaders.dictation.title')}</h2>
        <p className="module-subtitle">
          {t('moduleHeaders.dictation.progress')
            .replace('{total}', String(sentences.length))
            .replace('{correct}', String(correctCount))
            .replace('{checked}', String(checkedCount))}
        </p>
      </div>

      <ModuleTopExtras moduleId="dictation" />

      <div className="module-content">
        <button type="button" className="back-button" onClick={backToSetup} style={{ marginBottom: 12 }}>
          ← Back to edit passage
        </button>
        <div className="dict-toolbar">
          <button
            type="button"
            className={`btn btn-secondary btn-sm${slow ? ' dict-toggle-on' : ''}`}
            onClick={() => setSlow((v) => !v)}
            title="Play back more slowly"
          >
            🐢 Slow playback: {slow ? 'on' : 'off'}
          </button>
          <div className="dict-progress">
            <div className="dict-progress-bar">
              <div className="dict-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={clearAnswers}>
            ↺ Clear answers
          </button>
        </div>

        <ol className="dict-list">
          {sentences.map((s, i) => {
            const row = rows[i];
            const revealed = row?.revealed ?? false;
            const diff = revealed ? diffTokens(s, row.answer) : null;
            return (
              <li key={i} className="dict-row">
                <div className="dict-row-head">
                  <span className="dict-row-num">{i + 1}</span>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => speak(s)}
                    disabled={!speechSupported}
                  >
                    🔊 Listen
                  </button>
                  {revealed && diff && (
                    <span className={`dict-row-status ${diff.exact ? 'dict-status-ok' : 'dict-status-bad'}`}>
                      {diff.exact ? '✅ Correct' : '❌ Check below'}
                    </span>
                  )}
                  <button
                    type="button"
                    className={`btn btn-sm ${revealed ? 'btn-secondary' : 'btn-primary'}`}
                    onClick={() => toggleReveal(i)}
                    disabled={!row?.answer.trim()}
                  >
                    {revealed ? '✕ Hide answer' : '👀 Reveal answer'}
                  </button>
                </div>

                <input
                  ref={(el) => { inputRefs.current[i] = el; }}
                  type="text"
                  className="form-input dict-answer-input"
                  placeholder="Type what you hear…"
                  value={row?.answer ?? ''}
                  onChange={(e) => setAnswer(i, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && row.answer.trim()) toggleReveal(i);
                  }}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  aria-label={`Sentence ${i + 1} answer`}
                />

                {revealed && diff && (
                  <div className={`dict-result ${diff.exact ? 'dict-result-ok' : 'dict-result-bad'}`}>
                    <div className="dict-result-head">
                      {diff.exact ? '✅ Perfect!' : '❌ Not quite — compare:'}
                      <button
                        type="button"
                        className="dict-close-btn"
                        onClick={() => toggleReveal(i)}
                        title="Hide answer"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="dict-diff-line">
                      <span className="dict-diff-tag">Correct:</span>
                      <span className="dict-diff-text">
                        {diff.target.map((t, k) => (
                          <span key={k} className={t.cls === 'missing' ? 'dict-diff-missing' : undefined}>
                            {k > 0 ? tokenGap(diff.target[k - 1].text, t.text) : ''}{t.text}
                          </span>
                        ))}
                      </span>
                    </div>
                    {!diff.exact && (
                      <div className="dict-diff-line">
                        <span className="dict-diff-tag">Yours:</span>
                        <span className="dict-diff-text">
                          {diff.answer.length ? diff.answer.map((t, k) => (
                            <span key={k} className={t.cls === 'extra' ? 'dict-diff-extra' : undefined}>
                              {k > 0 ? tokenGap(diff.answer[k - 1].text, t.text) : ''}{t.text}
                            </span>
                          )) : <span className="dict-diff-extra">(empty)</span>}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>

        <div className="result-actions" style={{ justifyContent: 'center' }}>
          <button type="button" className="btn btn-secondary" onClick={clearAnswers}>↺ Clear answers</button>
          <button type="button" className="btn btn-primary" onClick={backToSetup}>New passage</button>
        </div>
      </div>
    </div>
  );
};

export default DictationModule;
